import { navigate } from "gatsby";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LOGGEDIN } from "../../../../constants/auth";
import {
  AddItemToCart,
  CreateCart,
  CreateCartWithCustomer,
} from "../../../../lib/dynamicQueries/cart";
import { updateCart } from "../../../../slices/cart";
import { getError } from "../../../../utils/errorCodes";
import { getSelectedVariant2 } from "../../../../utils/getCurrentVariant";
import { getPricingForProduct } from "../../../../utils/getPrice";
import { getDiscountPrice } from "../../../../utils/discountPrice";
import Addtocart from "../../../product/overview/addtocart";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import ProductRatingView from "../../../product/overview/Rating";
import SizeGuide from "./OverviewSizeGuide";
import { getVariantsById } from "../../../product/functions";
import ProductOverview from "./Overview";
import Variants from "../../../product/overview/variants";

const Details = ({
  product,
  clubPrice,
  onVariantChange,
  handleChangeVariant,
  selectedVariant,
  selectedColor,
  setSelectedColor,
  currentVariant,
}) => {
  const { authState, user } = useSelector((state) => state.auth);
  const { cartId } = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const [clubToolTip, setClubToolTip] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [variantsWithLiveStock, setVariantsWithLiveStock] = useState(null);

  useEffect(() => {
    setVariantsWithLiveStock([]);
    if (product && Array.isArray(product.variants)) {
      const fetchLiveStockData = async () => {
        try {
          const data = await getVariantsById(
            product.variants.map((variant) => variant.shopifyId)
          );
          setVariantsWithLiveStock(data);
        } catch (error) {
          console.error("Error fetching data:", error);
          // fall back to built version
          setVariantsWithLiveStock(product.variants);
        }
      };

      fetchLiveStockData();
    }
  }, []);

  useEffect(() => {
    if (!currentVariant) {
      return;
    }
    onVariantChange(currentVariant);
  }, [currentVariant]);

  async function handleAddToCart(e) {
    e.preventDefault();
    setLoading(true);
    let discountValue;
    if (clubPrice) {
      discountValue = await getDiscountPrice(
        currentVariant.contextualPricing.price.amount,
        clubPrice,
        1
      );
    }

    const variantData = {
      id: currentVariant.shopifyId,
      quantity: 1,
    };

    const response = cartId
      ? await AddItemToCart(cartId, variantData, discountValue)
      : authState === LOGGEDIN
      ? await CreateCartWithCustomer(variantData, user, discountValue)
      : await CreateCart(variantData, discountValue);

    if (response.cart) {
      dispatch(updateCart(response.cart));
      setLoading(false);
      setError("");
      navigate("/cart");
    } else {
      const err = getError(response.customerUserErrors?.[0]?.code);
      setError(err);
      setLoading(false);
    }
  }

  function colourIsAvailable(colour) {
    if (variantsWithLiveStock) {
      return variantsWithLiveStock.some((variant) =>
        variant.selectedOptions.find(
          (option) =>
            ["Colour", "Color"].includes(option.name) &&
            option.value === colour.name
        )
      );
    }
    return false;
  }

  return (
    <div className="w-full lg:w-1/2 pl-5 lg:pl-[50px] pr-5 box-border">
      <Dialog className="flex flex-col border-0">
        <div className="hidden md:block">
          <h1 className="pt-2 lg:pt-0 mb-2 text-xl md:text-[26px] md:leading-8 font-bold">
            {product.title}
          </h1>
          <div className="flex items-center cursor-pointer">
            <button
              onClick={() => navigate("reviews")}
              title="Product Rating"
              className="text-[13px] sm:font-bold leading-[17px] text-black/70 hover:text-gray-500
            transition-colors duration-200 ease-linear pp-no-hide"
            >
              <ProductRatingView
                reviewAverage={product.reviewsAverage?.value}
                reviewCount={product.reviewsCount?.value}
              />
            </button>
          </div>
          <ProductOverview handle={product.handle} />
        </div>

        <div className="max-w-[532px] mt-2 text-xl font-bold text-[#616161]">
          {currentVariant &&
            getPricingForProduct(
                currentVariant?.contextualPricing,
                currentVariant?.clubPrice,
                authState === LOGGEDIN,
                clubToolTip,
                () => setClubToolTip(false)
            )}
        </div>
        <hr className="max-w-[532px] mt-5" />
        {product?.colors?.length > 0 && (
          <p className="mb-[12px] mt-3 font-medium">
            <span className="font-bold">Colour:</span> {selectedColor?.name}
          </p>
        )}
        {product?.colors?.length > 0 && (
          <div className="flex gap-x-[9px] gap-y-[2px]">
            {product?.colors.filter(colourIsAvailable).map((color, index) => (
              <div
                key={index}
                onClick={() => setSelectedColor(color)}
                className={`aspect-[56/71] max-w-[56px] min-h-[71px] rounded-[5px] overflow-hidden ${
                  selectedColor === color
                    ? "border-2 border-solid border-black"
                    : "cursor-pointer"
                }`}
              >
                <img
                  src={color.imageUrl}
                  alt={color.name}
                  className="w-full h-full object-cover rounded-[5px]"
                />
              </div>
            ))}
          </div>
        )}
        {(product.fitType?.value || product.sizeGuide?.value) && (
          <div className="block mt-[13px] mb-[15px]">
            <div className="flex">
              <p>{product?.fitType?.value} </p>
              &nbsp;
              {product.sizeGuide?.value && (
                <>
                  {product?.fitType?.value && (
                    <span>
                      {" "}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="19"
                        height="22"
                        viewBox="0 0 19 22"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_156_12672)">
                          <path
                            d="M6.21188 -5.72205e-06H12.7891C13.0925 0.000337601 13.3833 0.139984 13.5979 0.388281C13.8124 0.636576 13.9331 0.973238 13.9336 1.32447V20.6749C13.9333 21.0262 13.8126 21.363 13.5981 21.6115C13.3835 21.8599 13.0926 21.9997 12.7891 22H6.21188C5.90845 21.9997 5.61752 21.8599 5.40296 21.6115C5.1884 21.363 5.06775 21.0262 5.06745 20.6749V1.32447C5.0679 0.973238 5.18863 0.636576 5.40317 0.388281C5.61771 0.139984 5.90855 0.000337601 6.21188 -5.72205e-06ZM12.7891 20.6964L12.8077 1.32447L6.21188 1.30362L6.19331 20.6749C6.19323 20.6777 6.19366 20.6806 6.19457 20.6832C6.19547 20.6859 6.19683 20.6883 6.19857 20.6903C6.20031 20.6923 6.20239 20.6939 6.20468 20.6949C6.20697 20.696 6.20942 20.6965 6.21188 20.6964H12.7891Z"
                            fill="black"
                          />
                          <path
                            d="M10.1328 18.21H13.3697C13.519 18.21 13.6622 18.2787 13.7677 18.401C13.8733 18.5232 13.9326 18.689 13.9326 18.8619C13.9326 19.0347 13.8733 19.2005 13.7677 19.3228C13.6622 19.445 13.519 19.5137 13.3697 19.5137H10.1328C9.98354 19.5137 9.84037 19.445 9.7348 19.3228C9.62923 19.2005 9.56991 19.0347 9.56991 18.8619C9.56991 18.689 9.62923 18.5232 9.7348 18.401C9.84037 18.2787 9.98354 18.21 10.1328 18.21Z"
                            fill="black"
                          />
                          <path
                            d="M11.2248 15.6026H13.3707C13.52 15.6026 13.6631 15.6713 13.7687 15.7935C13.8743 15.9158 13.9336 16.0816 13.9336 16.2544C13.9336 16.4273 13.8743 16.5931 13.7687 16.7153C13.6631 16.8376 13.52 16.9062 13.3707 16.9062H11.2248C11.0755 16.9062 10.9323 16.8376 10.8267 16.7153C10.7211 16.5931 10.6618 16.4273 10.6618 16.2544C10.6618 16.0816 10.7211 15.9158 10.8267 15.7935C10.9323 15.6713 11.0755 15.6026 11.2248 15.6026Z"
                            fill="black"
                          />
                          <path
                            d="M11.2248 12.9552H13.3707C13.52 12.9552 13.6631 13.0238 13.7687 13.1461C13.8743 13.2683 13.9336 13.4341 13.9336 13.607C13.9336 13.7798 13.8743 13.9456 13.7687 14.0679C13.6631 14.1901 13.52 14.2588 13.3707 14.2588H11.2248C11.0755 14.2588 10.9323 14.1901 10.8267 14.0679C10.7211 13.9456 10.6618 13.7798 10.6618 13.607C10.6618 13.4341 10.7211 13.2683 10.8267 13.1461C10.9323 13.0238 11.0755 12.9552 11.2248 12.9552Z"
                            fill="black"
                          />
                          <path
                            d="M10.1328 10.3878H13.3697C13.519 10.3878 13.6622 10.4565 13.7677 10.5787C13.8733 10.7009 13.9326 10.8667 13.9326 11.0396C13.9326 11.2125 13.8733 11.3782 13.7677 11.5005C13.6622 11.6227 13.519 11.6914 13.3697 11.6914H10.1328C9.98354 11.6914 9.84037 11.6227 9.7348 11.5005C9.62923 11.3782 9.56991 11.2125 9.56991 11.0396C9.56991 10.8667 9.62923 10.7009 9.7348 10.5787C9.84037 10.4565 9.98354 10.3878 10.1328 10.3878Z"
                            fill="black"
                          />
                          <path
                            d="M11.2248 7.86239H13.3707C13.52 7.86239 13.6631 7.93107 13.7687 8.05331C13.8743 8.17555 13.9336 8.34133 13.9336 8.5142C13.9336 8.68707 13.8743 8.85286 13.7687 8.97509C13.6631 9.09733 13.52 9.16602 13.3707 9.16602H11.2248C11.0755 9.16602 10.9323 9.09733 10.8267 8.97509C10.7211 8.85286 10.6618 8.68707 10.6618 8.5142C10.6618 8.34133 10.7211 8.17555 10.8267 8.05331C10.9323 7.93107 11.0755 7.86239 11.2248 7.86239Z"
                            fill="black"
                          />
                          <path
                            d="M11.2248 5.21493H13.3707C13.52 5.21493 13.6631 5.28361 13.7687 5.40585C13.8743 5.52809 13.9336 5.69387 13.9336 5.86674C13.9336 6.03961 13.8743 6.20539 13.7687 6.32763C13.6631 6.44987 13.52 6.51855 13.3707 6.51855H11.2248C11.0755 6.51855 10.9323 6.44987 10.8267 6.32763C10.7211 6.20539 10.6618 6.03961 10.6618 5.86674C10.6618 5.69387 10.7211 5.52809 10.8267 5.40585C10.9323 5.28361 11.0755 5.21493 11.2248 5.21493Z"
                            fill="black"
                          />
                          <path
                            d="M10.1328 2.56649H13.3697C13.519 2.56649 13.6622 2.63517 13.7677 2.75741C13.8733 2.87965 13.9326 3.04543 13.9326 3.2183C13.9326 3.39118 13.8733 3.55696 13.7677 3.6792C13.6622 3.80143 13.519 3.87012 13.3697 3.87012H10.1328C9.98354 3.87012 9.84037 3.80143 9.7348 3.6792C9.62923 3.55696 9.56991 3.39118 9.56991 3.2183C9.56991 3.04543 9.62923 2.87965 9.7348 2.75741C9.84037 2.63517 9.98354 2.56649 10.1328 2.56649Z"
                            fill="black"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_156_12672">
                            <rect
                              width="19"
                              height="22"
                              fill="white"
                              transform="matrix(-1 0 0 -1 19 22)"
                            />
                          </clipPath>
                        </defs>
                      </svg>{" "}
                    </span>
                  )}
                  <DialogTrigger
                    asChild
                    className="text-[#1f1f1f] underline underline-offset-4 cursor-pointer bg-transparent ml-1"
                  >
                    <span>Size guide</span>
                  </DialogTrigger>
                </>
              )}
            </div>
            {product.sizeGuide?.value && (
              <SizeGuide sizeGuide={product.sizeGuide?.value} />
            )}
          </div>
        )}
        {variantsWithLiveStock && (
          <Variants
            selectedVariant={selectedVariant}
            handleChangeVariant={handleChangeVariant}
            options={product.options}
            selectedColor={selectedColor}
            variants={variantsWithLiveStock}
            currentVariant={currentVariant}
          />
        )}
        <div className="my-4">
          {currentVariant?.availableForSale ? (
            <h6 className="text-[#279D3F] space-x-1 text-sm font-medium flex items-center">
              {" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
              >
                <circle cx="7.5" cy="7.5" r="7.5" fill="currentColor" />
              </svg>
              <span>In stock</span>
            </h6>
          ) : (
            <h6 className="text-red-600 space-x-1 text-sm font-medium flex items-center">
              {" "}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
              >
                <circle cx="7.5" cy="7.5" r="7.5" fill="currentColor" />
              </svg>
              <span>Out of stock</span>
            </h6>
          )}
        </div>

        <div className="mt-[23px] mb-[10px]">
          {/* Add to cart */}
          <Addtocart
            product={product}
            currentVariant={currentVariant}
            handleAddToCart={handleAddToCart}
            loading={loading}
          />
          {/* Wishlist */}
          {/* <Wishlist
            addedToWishlist={addedToWishlist}
            handleAddWishlist={handleAddWishlist}
          /> */}
        </div>
      </Dialog>
    </div>
  );
};

export default Details;
